import React, { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { IconInfoCircle } from '@tabler/icons-react';
import TopBar from '../../components/layout/TopBar';
import StickyFooter from '../../components/layout/StickyFooter';
import { Button, ErrorState, TextField } from '../../components/common';
import DoctorSummaryCard from '../../components/booking/DoctorSummaryCard';
import BookingScheduler from '../../components/booking/BookingScheduler';
import doctorApi from '../../api/doctor/doctorApi';
import appointmentApi from '../../api/appointment/appointmentApi';
import useFetch from '../../hooks/useFetch';
import useSlotPicker from '../../hooks/useSlotPicker';
import usePresetSlot from '../../hooks/usePresetSlot';
import { useClinic } from '../../context/ClinicContext';
import { PATHS, toPath } from '../../routes/paths';
import { formatDayDate, formatTime } from '../../utils/date';
import { formatCurrency } from '../../utils/format';

export default function BookingPage() {
  const { doctorId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { clinic } = useClinic();
  const { data: doctor, error, reload } = useFetch(() => doctorApi.get(doctorId), [doctorId]);
  const picker = useSlotPicker(doctorId, { bookingWindowDays: clinic?.bookingWindowDays });
  usePresetSlot(picker, location.state);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const { selectedDate, selectedTime } = picker;

  const confirm = async () => {
    setSubmitting(true);
    setSubmitError('');
    try {
      const appt = await appointmentApi.create({ doctorId, date: selectedDate, time: selectedTime, reason });
      navigate(toPath(PATHS.bookingSuccess, { id: appt.id }), { replace: true });
    } catch (e) {
      setSubmitError(e.message);
      setSubmitting(false);
      if (e.status === 409) picker.selectDate(selectedDate);
    }
  };

  if (error) {
    return (
      <div className="page">
        <TopBar title="Book appointment" bordered />
        <ErrorState error={error} onRetry={reload} />
      </div>
    );
  }

  return (
    <div className="page page--with-footer-lg booking">
      <TopBar title="Book appointment" bordered />
      <div className="page__body">
        <DoctorSummaryCard doctor={doctor} />

        <BookingScheduler picker={picker} />

        <section className="booking__section">
          <h2 className="booking__label">Reason for visit</h2>
          <p className="booking__hint">Optional — helps the doctor prepare</p>
          <TextField
            multiline
            rows={3}
            maxLength={300}
            placeholder="e.g. Follow-up on blood test results"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            aria-label="Reason for visit"
          />
        </section>

        {submitError && <div className="alert u-mt-4"><IconInfoCircle size={18} />{submitError}</div>}
      </div>

      <StickyFooter>
        <div className="sticky-footer__summary">
          <div>
            <p className="sticky-footer__label">In-clinic visit</p>
            <p className="sticky-footer__value">
              {selectedDate ? formatDayDate(selectedDate) : 'Pick a date'}
              {' · '}
              {selectedTime ? formatTime(selectedTime) : 'Pick a time'}
            </p>
          </div>
          <div className="u-text-right">
            <p className="sticky-footer__label">Fee</p>
            <p className="sticky-footer__value">{formatCurrency(doctor?.consultationFee, clinic?.currency)}</p>
          </div>
        </div>
        <Button block disabled={!selectedTime} loading={submitting} onClick={confirm}>
          {selectedTime ? 'Confirm booking' : 'Select a time'}
        </Button>
      </StickyFooter>
    </div>
  );
}
